import { ReactNode, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PUBLIC_SITE_LINKS, PublicSiteShell } from '@fias/public-site-ui';
import { RegisterDialog } from './RegisterDialog';
import { RegisterDialogContext } from './register-dialog-context';
import { LinkedInIcon, XIcon, YouTubeIcon } from './icons';
import { useGamesPageAvailable } from '@/lib/games-availability';
import {
  SITE_BASE_PATH,
  SUPPORT_EMAIL,
  SOCIAL_LINKS,
  BETA_PATH,
  DOCS_PATH,
  GAMES_PATH,
  type SiteBasePath,
} from '../content';

/**
 * Header + footer for the public marketing site, in the skeleton every
 * public Fias page shares (`PublicSiteShell`) — fias.io keeps its own look and
 * words inside it.
 *
 * The site deliberately carries its own chrome rather than `ArcheLayout`: it
 * renders both at the domain root (for signed-out visitors) and at
 * `/a/fias-site`, and in both places it should read as a website rather than
 * an app page. Keeping the chrome here also keeps the whole surface free of
 * auth hooks, which is what lets it prerender to static HTML.
 */

interface SiteChromeProps {
  /** Where the site is mounted, so internal links resolve correctly. */
  basePath?: SiteBasePath;
  children: ReactNode;
}

/** Keyed by the `icon` field on each entry in SOCIAL_LINKS. */
const SOCIAL_ICONS: Record<string, ReactNode> = {
  x: <XIcon />,
  linkedin: <LinkedInIcon />,
  youtube: <YouTubeIcon />,
};

function Wordmark({ basePath, className = '' }: { basePath: SiteBasePath; className?: string }) {
  return (
    <Link to={basePath} className={`inline-flex items-center ${className}`}>
      <img
        src="/fias-wordmark-light.png"
        alt="Fias"
        width={110}
        height={45}
        className="h-9 w-auto"
      />
    </Link>
  );
}

/**
 * Sends the viewer back to the top.
 *
 * Instant, not smooth: from the footer of a page this long a smooth scroll is
 * a several-second ride, and when the same click also changes route it would
 * still be animating while the new page renders underneath it.
 */
function scrollToTop() {
  window.scrollTo(0, 0);
}

export function SiteChrome({ basePath = SITE_BASE_PATH, children }: SiteChromeProps) {
  // Registration is gated behind the closed beta, so Register opens an
  // explainer rather than a dead end. Owned here so there is one dialog for
  // both the header and the hero.
  const [registerOpen, setRegisterOpen] = useState(false);
  const openRegisterDialog = useCallback(() => setRegisterOpen(true), []);
  const registerDialog = useMemo(() => ({ openRegisterDialog }), [openRegisterDialog]);
  // Asked after first paint, so the prerendered snapshot carries no Games link
  // and the page appears only where its switch is on.
  const showGames = useGamesPageAvailable() === true;

  return (
    <RegisterDialogContext.Provider value={registerDialog}>
      <PublicSiteShell
        className="fias-site font-sans"
        mainId="main"
        skipLinkClassName="fias-link sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:bg-[color:var(--fias-ink-panel)] focus:px-4 focus:py-2"
        // Floats over the hero so the ribbon runs behind the header — see
        // .fias-site-header. Pages carry enough top padding to clear it.
        headerClassName="fias-site-header"
        header={
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-6 px-6 py-5">
            <Wordmark basePath={basePath} />

            {/* No section nav: the page is one scroll, and the header's job is
                to get people into the product — signing in, registering, or
                playing (the public Games page, ADR-035). Home and Docs stay
                reachable from the footer. */}
            <div className="flex items-center gap-3">
              {showGames && (
                <Link to={GAMES_PATH} className="fias-link text-base">
                  Games
                </Link>
              )}
              <Link to={PUBLIC_SITE_LINKS.signIn} className="fias-link hidden text-base sm:inline">
                Login
              </Link>
              <button
                type="button"
                onClick={openRegisterDialog}
                className="fias-btn fias-btn-pill fias-btn-light"
              >
                Register
              </button>
            </div>
          </div>
        }
        footerClassName="mx-auto max-w-6xl px-6 pb-16"
        footer={
          <div className="fias-panel p-8 md:p-10">
            {/* Pill nav mirrors the header's language rather than the usual
                column of underlined links — the site has few enough
                destinations that they fit on one line. */}
            <div className="flex flex-wrap items-center justify-between gap-6">
              <nav aria-label="Footer" className="flex flex-wrap items-center gap-3">
                {/* Doubles as back-to-top. From the front page the route does
                    not change, so the scroll is the whole action; from a
                    sub-page it navigates AND lands at the top, which is where
                    someone clicking "Home" expects to arrive either way. */}
                <Link to={basePath} onClick={scrollToTop} className="fias-btn fias-btn-veil">
                  Home
                </Link>
                {showGames && (
                  <Link to={GAMES_PATH} className="fias-btn fias-btn-veil">
                    Games
                  </Link>
                )}
                <Link to={DOCS_PATH} className="fias-btn fias-btn-veil">
                  Docs
                </Link>
                <Link to={BETA_PATH} className="fias-btn fias-btn-primary">
                  Try Fias
                </Link>
              </nav>

              <ul className="flex items-center gap-3">
                {SOCIAL_LINKS.map((social) => (
                  <li key={social.label}>
                    <a
                      href={social.href}
                      className="fias-link fias-mock-row flex h-11 w-11 items-center justify-center"
                      rel="noreferrer noopener"
                      target="_blank"
                    >
                      <span className="h-[1.125rem] w-[1.125rem]">{SOCIAL_ICONS[social.icon]}</span>
                      <span className="sr-only">{social.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <hr className="fias-divider my-10" />

            <h2 className="font-sans text-xl font-bold tracking-[-0.02em] text-[color:var(--fias-bright)] md:text-[1.375rem]">
              contact:{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="fias-link !text-white underline">
                {SUPPORT_EMAIL}
              </a>
            </h2>

            <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
              <Wordmark basePath={basePath} className="opacity-70" />
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-sans text-xs">
                {/* Google's OAuth consent screen names these pages and
                    requires the home page to link them. */}
                <nav aria-label="Legal" className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <Link to={PUBLIC_SITE_LINKS.privacy} className="fias-link">
                    Privacy Policy
                  </Link>
                  <Link to={PUBLIC_SITE_LINKS.terms} className="fias-link">
                    Terms of Service
                  </Link>
                  <Link to={PUBLIC_SITE_LINKS.acceptableUse} className="fias-link">
                    Acceptable Use
                  </Link>
                </nav>
                <p className="text-[color:var(--fias-dim)]">
                  © {new Date().getFullYear()} Fias. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        }
        after={<RegisterDialog open={registerOpen} onClose={() => setRegisterOpen(false)} />}
      >
        {children}
      </PublicSiteShell>
    </RegisterDialogContext.Provider>
  );
}
